import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
  Link,
  Package,
  ScanSearch,
  Boxes,
  Warehouse,
  Banknote,
  TrendingUp,
  ShoppingCart,
} from "lucide-react";
import DatePicker from "react-datepicker";

import { useSite, useSiteMarketValue } from "../../hooks";

import { BestSellingProducts } from "./components";

import { Card, ModernCardHeader } from "../../../../components/cards";
import { formatNumber } from "../../../../helpers/string";
import { ProductsPreview } from "../../../products/components/products-preview";
import { useLazyScansBySite } from "../../../scans/hooks";
import { EmptyList, Loading } from "../../../../components";

import { TurnoverGraph } from "../../../scans/components/graphs";
import { SalesGraph } from "../../../scans/components/graphs/sales-graph";

type ISiteParams = {
  id: string;
};

export const Site: React.FC = () => {
  const { id } = useParams<ISiteParams>();
  const navigate = useNavigate();
  useEffect(() => {
    if (!id) {
      navigate("/sites");
    }
  }, []);

  let [startDate, setStartDate] = React.useState<Date | null>(null);
  let [endDate, setEndDate] = React.useState<Date | null>(null);
  let [groupBy, setGroupBy] = React.useState<string>("day");
  const { data, loading, refetch } = useSite(+id!);
  const { data: marketValueData } = useSiteMarketValue(+id!);
  const [searchScans, { data: scansData, loading: scansLoading }] =
    useLazyScansBySite();

  console.log({ data });

  React.useEffect(() => {
    searchScans({
      variables: {
        siteId: +id!,
        where: {
          start: startDate?.toISOString(),
          end: endDate?.toISOString(),
        },
        groupBy,
      },
      fetchPolicy: "network-only",
    });
  }, []);

  React.useEffect(() => {
    searchScans({
      variables: {
        siteId: +id!,
        where: {
          start: startDate?.toISOString(),
          end: endDate?.toISOString(),
        },
        groupBy,
      },
      fetchPolicy: "network-only",
    });
  }, [startDate, endDate, groupBy]);

  return (
    <>
      {/* Header with site info */}
      <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200 mb-2">
        <div className="main-container pt-4">
          <div className="flex items-center justify-between">
            <div className="flex ">
              <h1 className="text-xl font-bold text-gray-900 mr-2">
                {data?.site?.result?.name ?? "Chargement..."}
              </h1>
              <div className="flex gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Link size={14} className="text-gray-500" />
                  <span className="truncate max-w-xs">
                    {data?.site?.result?.url}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Filters sticky bar */}
        <div className="sticky z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="main-container pb-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Filtres:
              </span>
              <div className="flex gap-3 flex-1">
                <div className="flex-1">
                  <DatePicker
                    locale={"fr"}
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    placeholderText="Date début"
                    className="input w-full text-sm"
                    wrapperClassName="w-full"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <div className="flex-1">
                  <DatePicker
                    locale={"fr"}
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    placeholderText="Date fin"
                    className="input w-full text-sm"
                    wrapperClassName="w-full"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <div className="w-40">
                  <select
                    className="input w-full text-sm"
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                  >
                    <option value="day">Par jour</option>
                    <option value="week">Par semaine</option>
                    <option value="month">Par mois</option>
                    <option value="year">Par année</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="main-container px-4 ">
        <div className="section">
          {/* Stats Cards */}
          <div className="w-full mb-2">
            <div className="grid grid-cols-2 xl:grid-cols-5 gap-2">
              <Card
                hover
                padding="sm"
                className="shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Nombre de produits
                    </p>
                    <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                      {formatNumber(
                        marketValueData?.siteMarketValue?.totalProducts
                      )}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                      Nb. produit disponibles
                    </p>
                  </div>
                  <div className="bg-blue-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Boxes className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card
                hover
                padding="sm"
                className="shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Etat du stock
                    </p>
                    <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                      {formatNumber(
                        marketValueData?.siteMarketValue?.totalQuantity
                      )}{" "}
                      pcs
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                      Nb. produits en stock
                    </p>
                  </div>
                  <div className="bg-amber-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Warehouse className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                  </div>
                </div>
              </Card>

              <Card
                hover
                padding="sm"
                className="shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Valeur du stock
                    </p>
                    <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                      {formatNumber(
                        marketValueData?.siteMarketValue?.marketValue
                          ? Math.round(
                              marketValueData.siteMarketValue.marketValue * 100
                            ) / 100
                          : 0
                      )}{" "}
                      €
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                      Valeur estimative
                    </p>
                  </div>
                  <div className="bg-emerald-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Banknote className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                  </div>
                </div>
              </Card>

              <Card
                hover
                padding="sm"
                className="shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Chiffre d'affaire
                    </p>
                    <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                      {formatNumber(
                        scansData?.scansBySite?.results?.reduce(
                          (acc, scan) => acc + (scan.ca ?? 0),
                          0
                        )
                      )}{" "}
                      €
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                      Total des ventes
                    </p>
                  </div>
                  <div className="bg-primary-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                  </div>
                </div>
              </Card>

              <Card
                hover
                padding="sm"
                className="shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Produits Vendus
                    </p>
                    <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
                      {formatNumber(
                        scansData?.scansBySite?.results?.reduce(
                          (acc, scan) => acc + (scan.salesSum ?? 0),
                          0
                        )
                      )}{" "}
                      pcs
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">
                      Nombre total de produits
                    </p>
                  </div>
                  <div className="bg-rose-50 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" />
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Graph Cards */}
          <div className="w-full md:w-1/2 pr-1">
            <Card
              padding="md"
              className="mb-3 shadow-sm border border-gray-200"
            >
              <div className="mb-4">
                <h3 className="text-base font-semibold text-gray-900">
                  Chiffre d'affaire
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Évolution des ventes
                </p>
              </div>
              <div className="mt-3">
                {scansLoading ? <Loading /> : null}
                {scansData?.scansBySite?.results?.length === 0 ? (
                  <EmptyList text="Aucune donnée disponible" />
                ) : null}
                {scansData ? (
                  <TurnoverGraph
                    data={scansData?.scansBySite?.results}
                    onSelect={(data) => {
                      console.log(data);
                      setStartDate(new Date(data.createdAt));
                      setEndDate(new Date(data.createdAt));
                    }}
                  />
                ) : null}
              </div>
            </Card>
          </div>

          <div className="w-full md:w-1/2 pl-1">
            <Card
              padding="md"
              className="mb-3 shadow-sm border border-gray-200"
            >
              <div className="mb-4">
                <h3 className="text-base font-semibold text-gray-900">
                  Produits vendus
                </h3>
                <p className="text-xs text-gray-500 mt-1">Volume des ventes</p>
              </div>
              <div className="mt-3">
                {scansLoading ? <Loading /> : null}
                {scansData?.scansBySite?.results?.length === 0 ? (
                  <EmptyList text="Aucune donnée disponible" />
                ) : null}
                {scansData ? (
                  <SalesGraph
                    data={scansData?.scansBySite?.results}
                    onSelect={(data) => {
                      console.log(data);
                      setStartDate(new Date(data.createdAt));
                      setEndDate(new Date(data.createdAt));
                    }}
                  />
                ) : null}
              </div>
            </Card>
          </div>

          <BestSellingProducts
            siteId={+id!}
            start={startDate?.toISOString()}
            end={endDate?.toISOString()}
          />

          <div className="w-full">
            <Card padding="md" className="shadow-sm">
              <ModernCardHeader title="Produits" />
              <div className="mt-3">
                <ProductsPreview siteId={+id!} />
              </div>
            </Card>
          </div>

          {/* <Recap siteId={+id!} />/ */}
        </div>
      </div>
    </>
  );
};
